import { SetStateAction, useEffect, useState } from "react";
import { X } from "lucide-react";
import { getAllReviews } from "../services/api";
import { toast } from "react-toastify";

type Review = {
  comment: string;
  client_name: any;
  media_url: string | undefined;
  id: string;
  videoUrl: string;
  content: string;
  clientName: string;
  thumbnailUrl: string;
};

const Reviews = () => {
  const [reviews, setReviews] = useState<Review[]>([]) as any;
  const [selectedReview, setSelectedReview] = useState<Review | null>(null);

  // Fetch reviews on component mount
  useEffect(() => {
    fetchReviews();
  }, []);

  const fetchReviews = async () => {
    try {
      const response = (await getAllReviews()) as any;
      setReviews(response.data.data);
    } catch (error) {
      console.error("Error fetching reviews:", error);
      toast.error("Failed to fetch reviews.", {
        position: "top-center",
        autoClose: 3000,
      });
    }
  };

  const handleNextReview = () => {
    if (!selectedReview) return;
    const currentIndex = reviews.findIndex(
      (review: { id: string }) => review.id === selectedReview.id
    );
    const nextIndex = (currentIndex + 1) % reviews.length;
    setSelectedReview(reviews[nextIndex]);
  };

  const handlePreviousReview = () => {
    if (!selectedReview) return;
    const currentIndex = reviews.findIndex(
      (review: { id: string }) => review.id === selectedReview.id
    );
    const previousIndex = (currentIndex - 1 + reviews.length) % reviews.length;
    setSelectedReview(reviews[previousIndex]);
  };

  return (
    <div className="pb-6 md:pb-8 lg:pb-12 mt-14 md:mt-14 lg:mt-[4.5rem]">
      {/* Header - Made responsive */}
      <div className="bg-[#FBEFEF] w-full flex items-center justify-center py-6 md:py-8 lg:py-12">
        <img
          src="/images/reviewText.svg"
          alt="Reviews"
          className="w-[150px] md:w-[200px] lg:w-[200px]"
        />
      </div>

      <div className="max-w-5xl mx-auto px-4 md:px-6 lg:px-8">
        {/* Reviews Grid - Made responsive */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 lg:gap-[48px] pt-6 md:pt-8 lg:pt-10 justify-items-center">
          {reviews.map((review: any) => (
            <div
              key={review.id}
              className="bg-white rounded-lg border border-gray-200 overflow-hidden cursor-pointer transform transition-transform w-full md:max-w-[307px] hover:shadow-lg"
              onClick={() => setSelectedReview(review)}
            >
              <div className="hidden relative aspect-video bg-gray-100 overflow-hidden group">
                <img
                  src={review.media_url}
                  alt={`${review.client_name}'s review`}
                  className="w-full h-full object-cover transform transition-transform duration-300 group-hover:scale-105"
                />
                {[".mp4", ".mov", ".webm", ".avi"].some((ext) =>
                  review.media_url.toLowerCase().endsWith(ext)
                ) && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <img
                      src="/images/videoplayer.svg"
                      alt="Play"
                      className="w-12 h-12 md:w-16 md:h-16 lg:w-auto lg:h-auto"
                    />
                  </div>
                )}
              </div>

              <div className="p-4 md:p-5 lg:p-6">
                <p className="text-gray-800 mb-3 md:mb-4 italic text-sm md:text-base">
                  {review.comment}
                </p>
                <p className="text-right text-rose-800 text-sm md:text-base">
                  ~ {review.client_name}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Modal - Made responsive */}
        {selectedReview && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 md:p-6 lg:p-0">
            <div className="bg-[#FBEFEF] rounded-lg w-full max-w-[90vw] md:max-w-[80vw] lg:max-w-5xl relative h-[400px] md:h-[500px] lg:h-[658px]">
              {/* Close button */}
              <button
                onClick={() => setSelectedReview(null)}
                className="absolute right-2 top-2 md:right-4 md:top-4 text-gray-500 hover:text-gray-700 z-30"
              >
                <X className="w-5 h-5 md:w-6 md:h-6" />
              </button>

              {/* Decorative images - Hidden on mobile, visible on larger screens */}
              <div className="hidden lg:block absolute top-0 right-0">
                <img src="/images/floralone.svg" alt="" />
              </div>
              <div className="hidden lg:block absolute top-0 left-0">
                <img src="/images/floralLeft.svg" className="h-4" alt="" />
              </div>
              <div className="hidden lg:block absolute top-0 left-0">
                <img src="/images/floralLeftTwo.svg" alt="" />
              </div>
              <div className="hidden lg:block absolute bottom-0 right-0">
                <img src="/images/floralBottom.svg" alt="" />
              </div>
              <div className="hidden lg:block absolute bottom-[20px] left-0">
                <img src="/images/floralBottomLeft.svg" alt="" />
              </div>

              {/* Content container */}
              <div className="h-full p-4 md:p-8 lg:p-12 flex flex-col lg:flex-row w-full relative">
                {/* Navigation buttons */}
                <button
                  onClick={handlePreviousReview}
                  className="absolute left-2 md:left-4 lg:left-[-100px] top-1/2 transform -translate-y-1/2 bg-[#0000004D] p-2 md:p-3 rounded-full shadow hover:bg-[#B87775] transition z-20"
                >
                  <img
                    src="/images/leftarrow.svg"
                    alt="Previous"
                    className="w-4 h-4 md:w-6 md:h-6 lg:w-auto lg:h-auto"
                  />
                </button>

                {/* Image container */}
                <div className="relative rounded-lg w-full lg:w-[504px] h-[200px] md:h-[300px] lg:h-full">
                  {[".mp4", ".mov", ".webm", ".avi"].some((ext) =>
                    selectedReview.media_url?.toLowerCase().endsWith(ext)
                  ) ? (
                    <div className="relative h-full w-full">
                      <video
                        src={selectedReview.media_url}
                        className="h-full w-full object-cover rounded-lg"
                      />
                      <div className="absolute inset-0 flex items-center justify-center">
                        <img
                          src="/images/videoplayer.svg"
                          alt="Play"
                          className="w-12 h-12 md:w-16 md:h-16"
                        />
                      </div>
                    </div>
                  ) : (
                    <img
                      src={selectedReview.media_url}
                      alt={`${selectedReview.client_name}'s review`}
                      className="h-full w-full object-cover rounded-lg"
                    />
                  )}
                </div>

                {/* Text content */}
                <div className="flex items-center justify-center w-full lg:w-[338px] mx-auto flex-col mt-4 lg:mt-0">
                  <p className="text-base md:text-lg text-[18px] mb-2 md:mb-3 lg:mb-4 italic text-center px-4">
                    {selectedReview.comment}
                  </p>
                  <p className="text-center text-lg md:text-xl lg:text-[24px] font-[500]">
                    ~ {selectedReview.client_name}
                  </p>
                </div>

                {/* Right navigation button */}
                <button
                  onClick={handleNextReview}
                  className="absolute right-2 md:right-4 lg:right-[-100px] top-1/2 transform -translate-y-1/2 bg-[#0000004D] p-2 md:p-3 rounded-full shadow hover:bg-[#B87775] transition z-20"
                >
                  <img
                    src="/images/rightarrow.svg"
                    alt="Next"
                    className="w-4 h-4 md:w-6 md:h-6 lg:w-auto lg:h-auto"
                  />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Reviews;
